import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2Icon, CompassIcon, HeartIcon, MessageCircleIcon, ShieldIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Container } from '../../components/ui/Container';
import { GeometricAccent } from '../../components/ui/GeometricAccent';
import { MosqueArchMask } from '../../components/ui/MosqueArchMask';
import { PatternDecor } from '../../components/ui/PatternDecor';
type Tone = 'sage' | 'sky' | 'rose' | 'sand';
const toneClasses: Record<Tone, string> = {
  sage: 'border-forest-700/10 bg-sage-100',
  sky: 'border-[#C7D4E0]/40 bg-[#EDF2F7]',
  rose: 'border-[#E0BFB3]/40 bg-[#F7EDE9]',
  sand: 'border-sand-300/40 bg-sand-100'
};
const skills = [{
  icon: HeartIcon,
  title: 'الوعي بالذات',
  description: 'يتعلم الأطفال فهم مشاعرهم وقيمهم وردود أفعالهم قبل الاستجابة للآخرين.',
  tone: 'sage' as const
}, {
  icon: MessageCircleIcon,
  title: 'تواصل باحترام',
  description: 'أدوات للتعامل مع الأقران والبالغين من خلفيات مختلفة بهدوء وثقة.',
  tone: 'sky' as const
}, {
  icon: ShieldIcon,
  title: 'حدود صحية',
  description: 'معرفة متى يقول الطفل لا، وكيف يختلف باحترام، وكيف يطلب المساعدة عند الحاجة.',
  tone: 'rose' as const
}, {
  icon: CompassIcon,
  title: 'اتخاذ القرارات الأخلاقية',
  description: 'أطر عملية للاختيارات اليومية، متجذرة في القيم ومطبقة في الحياة الواقعية.',
  tone: 'sand' as const
}];
const identityPrinciples = ['الهوية قوة وليست عائقًا أبدًا', 'الثقة تُعلَّم من خلال القدوة واللغة والممارسة', 'ينبغي أن يحترم الأطفال أنفسهم والآخرين على قدم المساواة', 'يُعترف بالاختلاف بهدوء، لا يُتجنب ولا يُضخَّم', 'يُعاش الإيمان بإخلاص من خلال العبادة وحسن الخلق'];
export function ArabicValuesIdentity() {
  return <div dir="rtl" className="overflow-hidden text-right" data-id="element-3198" data-mp="2wlh7pneeslft">
      <section className="relative overflow-hidden" data-id="element-3199" data-mp="31hxykpcztdwl">
        <GeometricAccent variant="arabesque" className="absolute -right-20 top-6 hidden h-[420px] w-[420px] md:block" opacity={0.08} data-id="element-3200" data-mp="31kkebca1ezpw" />
        <GeometricAccent variant="dots" className="absolute left-8 top-16 h-32 w-32" opacity={0.4} data-id="element-3201" data-mp="3ppjn03exqze" />
        <Container className="relative pb-16 pt-8 md:pb-24 md:pt-12" data-id="element-3202" data-mp="hp7adl09rebe">
          <div className="grid items-center gap-10 lg:grid-cols-12 lg:gap-8" data-id="element-3203" data-mp="cpubg1w4igsg">
            <motion.div initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            ease: [0.22, 1, 0.36, 1]
          }} className="flex flex-col gap-6 lg:col-span-6" data-id="element-3204" data-mp="cryphcnqkw6g">
              <span className="text-[15px] font-semibold text-forest-700" data-id="element-3205" data-mp="mns0grlt4hm4">هوية قوية. ثقة هادئة. اندماج محترم.</span>
              <h1 className="font-display text-4xl font-semibold leading-[1.45] tracking-tight text-ink-900 sm:text-5xl lg:text-[3.5rem]" data-id="element-3206" data-mp="10h9jmbakzsbm">القيم والهوية</h1>
              <div className="max-w-xl space-y-3 text-base leading-loose text-ink-700 md:text-lg" data-id="element-3207" data-mp="1aenvryph1uy4">
                <p data-id="element-3208" data-mp="15fuk8btqwe0l">نؤمن بأن الهوية الإسلامية ينبغي أن تكون مصدر قوة، لا خوفًا من الذات ولا عزلة ولا ضغطًا.</p>
                <p data-id="element-3209" data-mp="1p0us0jyn8tn3">يساعد عملنا الأطفال على أن ينشؤوا مسلمين واثقين يحترمون الآخرين ويفهمون أنفسهم ويتعاملون مع الاختلافات الثقافية بنضج وسلام.</p>
              </div>
              <div className="flex flex-wrap gap-3 pt-2" data-id="element-3210" data-mp="1f2ne69znirpb">
                <Link to="/ar/children/kids-programs" data-id="element-3211" data-mp="1f487jwa8c4ll"><Button variant="primary" size="lg" data-id="element-3212" data-mp="1k0p5tjb57zce">استكشف البرامج</Button></Link>
                <Link to="/ar/children/scholarships" data-id="element-3213" data-mp="169trvslses1y"><Button variant="secondary" size="lg" data-id="element-3214" data-mp="168shzrgicblt">المنح والدعم</Button></Link>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.7,
            delay: 0.1,
            ease: [0.22, 1, 0.36, 1]
          }} className="relative lg:col-span-6" data-id="element-3215" data-mp="1b1v3dcb4seqx">
              <div className="relative aspect-[200/148] w-full lg:-mr-8 lg:w-[calc(100%+6rem)]" data-id="element-3216" data-mp="11b0es1aex32b">
                <MosqueArchMask src="https://cdn.magicpatterns.com/uploads/f2BL8YAy91ePHt862eZ6yr/Values.jpg" alt="أب مسلم وابنه يقرآن قصة معًا في المنزل" data-id="element-3217" data-mp="nbranzzhdzep" />
              </div>
            </motion.div>
          </div>
        </Container>
      </section>

      <section className="bg-sage-50/60 py-16 md:py-24" data-id="element-3218" data-mp="f9qfp6nauzi7">
        <Container data-id="element-3219" data-mp="fccvftkcglbi">
          <h2 className="max-w-3xl font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-5xl" data-id="element-3220" data-mp="ajjrh7wp9h0h">تُبنى الهوية عبر مهارات الحياة اليومية</h2>
          <p className="mt-5 max-w-4xl leading-loose text-ink-700" data-id="element-3221" data-mp="5jxe8ygvg0ro">لا يطوّر الأطفال ثقتهم من خلال المعلومات وحدها؛ بل يطوّرونها بالإرشاد واللغة والحدود والمرونة العاطفية، حين يمارسونها باستمرار في مواقف الحياة اليومية.</p>
          <div className="mt-10 grid gap-5 md:grid-cols-2" data-id="element-3222" data-mp="3sr99z8w8n2r1">
            {skills.map((skill, index) => {
            const Icon = skill.icon;
            return <motion.article key={skill.title} initial={{
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-60px'
            }} transition={{
              duration: 0.5,
              delay: index * 0.06
            }} className={`flex gap-4 rounded-2xl border p-6 ${toneClasses[skill.tone]}`} data-id="element-3223" data-mp="3isa6s5u8rzbf" data-mpi={index}>
                  <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-forest-700/10 bg-white text-forest-700" data-id="element-3224" data-mp="1pfl3nxd9uaka"><Icon size={18} strokeWidth={1.6} aria-hidden="true" data-id="element-3225" data-mp="1tg50eztxnc5w" /></span>
                  <div data-id="element-3226" data-mp="1y9pz6u3coa65"><h3 className="font-display text-lg font-semibold leading-loose text-ink-900" data-id="element-3227" data-mp="1y9px8ojsi03i">{skill.title}</h3><p className="mt-1 text-sm leading-loose text-ink-700" data-id="element-3228" data-mp="2379cnrs84ix1">{skill.description}</p></div>
                </motion.article>;
          })}
          </div>
        </Container>
      </section>

      <section className="relative isolate overflow-hidden bg-white/60 py-16 md:py-24" data-id="element-3229" data-mp="1alg7jbjggs73">
        <PatternDecor side="right" y="top" size="lg" data-id="element-3230" data-mp="1fh5alouoor85" />
        <Container data-id="element-3231" data-mp="1khtal00w4iei">
          <div className="grid items-center gap-12 lg:grid-cols-12" data-id="element-3232" data-mp="1kichzmswzoea">
            <div className="lg:col-span-6" data-id="element-3233" data-mp="2qy4zeahtonxf">
              <h2 className="font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-4xl" data-id="element-3234" data-mp="2qx3rgew3shjx">نهجنا في بناء الهوية</h2>
              <ul className="mt-6 space-y-4 text-ink-700" data-id="element-3235" data-mp="268kl9fqo9x9o">
                {identityPrinciples.map(principle => <li key={principle} className="flex items-start gap-3 leading-loose" data-id="element-3236" data-mp="219zknf7idbkp">
                    <CheckCircle2Icon size={18} className="mt-1 shrink-0 text-forest-700" strokeWidth={1.6} aria-hidden="true" data-id="element-3237" data-mp="2g6gv847vim67" />
                    <span data-id="element-3238" data-mp="2b8p4hdiyt63l">{principle}</span>
                  </li>)}
              </ul>
            </div>
            <div className="lg:col-span-6" data-id="element-3239" data-mp="1nil0h51v74c2">
              <div className="aspect-[4/3] overflow-hidden rounded-3xl shadow-card" data-id="element-3240" data-mp="1ij0492e2xa31">
                <img src="https://cdn.magicpatterns.com/uploads/82e2kLQDrubbGnRrEfyVgg/identuty.jpg" alt="طفلة مسلمة تكتب إلى جانب نافذة مضيئة" className="h-full w-full object-cover" data-id="element-3241" data-mp="1ih5glgbfb3v9" />
              </div>
            </div>
          </div>
        </Container>
      </section>

      <section className="relative isolate overflow-hidden bg-white/60 py-16 md:py-24" data-id="element-3242" data-mp="14q0nyy6h3buf">
        <PatternDecor side="left" y="middle" size="md" data-id="element-3243" data-mp="zrpe4ng6h1c7" />
        <Container data-id="element-3244" data-mp="3bg3xtrapaprf">
          <div className="flex flex-col gap-8 md:flex-row md:items-end md:justify-between" data-id="element-3245" data-mp="3bfkdy51hprar">
            <div className="max-w-xl" data-id="element-3246" data-mp="3g6u3z6qyawn6">
              <h2 className="font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-5xl" data-id="element-3247" data-mp="3g7vg7ejpdp6h">هل أنتم مستعدون للبدء؟</h2>
              <p className="mt-4 leading-loose text-ink-700" data-id="element-3248" data-mp="36gr2ei5rkad8">اختر برنامجًا يناسب عمر طفلك واحتياجاته، وابدأ ببناء الثقة والقيم والانتماء في مساحة تعليمية آمنة.</p>
            </div>
            <div className="flex flex-wrap gap-3" data-id="element-3249" data-mp="3u48vch0rlehs">
              <Link to="/ar/children/kids-programs" data-id="element-3250" data-mp="30afr80q1689"><Button variant="primary" data-id="element-3251" data-mp="2z94p3jpgjqj">عرض برامج الأطفال</Button></Link>
              <Link to="/ar/children/scholarships" data-id="element-3252" data-mp="3l4vlvocn4px2"><Button variant="secondary" data-id="element-3253" data-mp="3l7q9tpuv4sv8">التقدّم بطلب منحة</Button></Link>
            </div>
          </div>
        </Container>
      </section>
    </div>;
}